import React from "react";
import { CheckCircle2, AlertCircle, Clock, ChevronRight } from "lucide-react";

const STATUS_STYLES = {
  configured: {
    badge: "bg-emerald-50 text-emerald-700 border-emerald-200",
    icon: CheckCircle2,
    iconColor: "text-emerald-500",
    label: "Configured",
  },
  not_configured: {
    badge: "bg-amber-50 text-amber-700 border-amber-200",
    icon: AlertCircle,
    iconColor: "text-amber-500",
    label: "Not Configured",
  },
};

function formatTimestamp(dt) {
  if (!dt) return null;
  try {
    return new Intl.DateTimeFormat("en-IN", {
      dateStyle: "medium",
      timeZone: "Asia/Kolkata",
    }).format(new Date(dt));
  } catch {
    return null;
  }
}

export default function ConfigCard({ category, status, metadata, onClick }) {
  const Icon = category.icon;
  const statusInfo = STATUS_STYLES[status] || STATUS_STYLES.not_configured;
  const StatusIcon = statusInfo.icon;
  const lastUpdated = formatTimestamp(metadata?.updated_date_time);

  return (
    <button
      type="button"
      onClick={onClick}
      className="group relative w-full text-left bg-white rounded-xl sm:rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-md hover:border-slate-300 active:scale-[0.98] sm:hover:-translate-y-0.5 transition-all duration-200 p-4 sm:p-5 flex flex-col gap-2.5 sm:gap-3 min-h-[160px] sm:min-h-[180px] focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:ring-offset-2 touch-manipulation"
    >
      {/* Icon + Status */}
      <div className="flex items-start justify-between gap-2">
        <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-lg sm:rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100/60 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
          <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
        </div>
        <span className={`inline-flex items-center gap-1 px-1.5 sm:px-2 py-0.5 rounded-full text-[10px] sm:text-[11px] font-medium border whitespace-nowrap ${statusInfo.badge}`}>
          <StatusIcon className={`w-2.5 h-2.5 sm:w-3 sm:h-3 ${statusInfo.iconColor}`} />
          {statusInfo.label}
        </span>
      </div>

      {/* Title + Description */}
      <div className="flex-1 min-w-0">
        <h3 className="text-[13px] sm:text-sm font-semibold text-slate-800 group-hover:text-blue-700 transition-colors leading-tight">
          {category.label}
        </h3>
        <p className="mt-0.5 text-[11px] sm:text-xs text-slate-500 line-clamp-2 leading-relaxed">
          {category.description}
        </p>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pt-2 border-t border-slate-100">
        {lastUpdated ? (
          <span className="inline-flex items-center gap-1 text-[10px] sm:text-[11px] text-slate-400">
            <Clock className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
            <span className="truncate max-w-[120px] sm:max-w-none">{lastUpdated}</span>
          </span>
        ) : (
          <span className="text-[10px] sm:text-[11px] text-slate-300 italic">No activity yet</span>
        )}
        <ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-300 group-hover:text-blue-500 group-hover:translate-x-0.5 transition-all shrink-0" />
      </div>
    </button>
  );
}
